import React, { useEffect, createContext, useContext, useState, useRef } from "react"
import { useRouter } from "next/router"
import Script from "next/script"
import analytics, { AnalyticsAccountType } from "../../services/analytics"
import { enqueueEvent } from "../../utils/internalAnalyticsQueue"
import { useUserContext } from "../../context/UserContext"
import { clarity } from "react-microsoft-clarity"

/**
 * Analytics configuration types
 */
interface PlatformAnalyticsConfig {
    googleAnalytics?: {
        trackingId: string
    }
    googleAnalyticsSecondary?: {
        trackingId: string
    }
    facebookPixel?: {
        pixelId: string
    }
    tiktokPixel?: {
        pixelId: string
    }
    snapchatPixel?: {
        pixelId: string
    }
    clarity?: {
        projectId: string
    }
    posthog?: {
        apiKey: string
        apiHost?: string
    }
}

interface EventAnalyticsConfig {
    facebookPixel?: {
        pixelId: string
    }
    tiktokPixel?: {
        pixelId: string
    }
    snapchatPixel?: {
        pixelId: string
    }
    googleAnalytics?: {
        trackingId: string
    }
}

/**
 * Client-side cache for analytics configs
 * Module-level variables to persist across re-renders
 * Includes TTL to reduce API calls
 */

interface CacheEntry<T> {
    data: T
    timestamp: number
}

const CACHE_TTL = 10 * 60 * 1000 // 10 minutes client-side cache

let cachedPlatformConfig: PlatformAnalyticsConfig | null = null
let platformConfigCacheEntry: CacheEntry<PlatformAnalyticsConfig> | null = null
const eventConfigCache = new Map<string, EventAnalyticsConfig>()
const eventConfigCacheWithTTL = new Map<string, CacheEntry<EventAnalyticsConfig>>()

/**
 * Check if cache entry is still valid
 */
function isCacheValid<T>(cache: CacheEntry<T> | null): boolean {
    if (!cache) return false
    return Date.now() - cache.timestamp < CACHE_TTL
}

/**
 * Get cached platform config if valid, otherwise null
 */
function getCachedPlatformConfig(): PlatformAnalyticsConfig | null {
    if (isCacheValid(platformConfigCacheEntry)) {
        return platformConfigCacheEntry!.data
    }
    return null
}

/**
 * Set cached platform config
 */
function setCachedPlatformConfig(data: PlatformAnalyticsConfig): void {
    platformConfigCacheEntry = {
        data,
        timestamp: Date.now(),
    }
    cachedPlatformConfig = data
}

/**
 * Get cached event config if valid, otherwise null
 */
function getCachedEventConfig(eventId: string): EventAnalyticsConfig | null {
    const entry = eventConfigCacheWithTTL.get(eventId) ?? null
    if (isCacheValid(entry)) {
        return entry!.data
    }
    return null
}

/**
 * Set cached event config
 */
function setCachedEventConfig(eventId: string, data: EventAnalyticsConfig): void {
    eventConfigCacheWithTTL.set(eventId, {
        data,
        timestamp: Date.now(),
    })
    eventConfigCache.set(eventId, data)
}

/**
 * Tracks which script IDs have already been rendered to prevent duplicate
 * <Script> tags across re-renders (request deduplication)
 */
const loadedScripts = new Set<string>()

const buildFacebookPixelScript = (pixelId: string) => `
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
  window.fbq('init', '${pixelId}');
  window.fbq('track', 'PageView');
}
`

const buildTikTokPixelScript = (pixelId: string) => `
!function (w, d, t) {
  w.TiktokAnalyticsObject=t;
  var ttq=w[t]=w[t]||[];
  ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];
  ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
  for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
  ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};
  ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";
  ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};
  var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;
  var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
  if (typeof w[t].load === "function") {
    w[t].load('${pixelId}');
  }
  if (typeof w[t].page === "function") {
    w[t].page();
  }
}(window, document, 'ttq');
`

/**
 * Fallback to environment variables during migration period
 */
function getFallbackPlatformConfig(): PlatformAnalyticsConfig {
    return {
        googleAnalytics: process.env.NEXT_PUBLIC_GA_TRACKING_ID
            ? { trackingId: process.env.NEXT_PUBLIC_GA_TRACKING_ID }
            : undefined,
        googleAnalyticsSecondary: process.env.NEXT_PUBLIC_PASSTICKETS_COM_ANALYTICS
            ? { trackingId: process.env.NEXT_PUBLIC_PASSTICKETS_COM_ANALYTICS }
            : undefined,
        facebookPixel: process.env.NEXT_PUBLIC_PASSTICKETS_FACEBOOK_PIXEL_KEY
            ? { pixelId: process.env.NEXT_PUBLIC_PASSTICKETS_FACEBOOK_PIXEL_KEY }
            : undefined,
        tiktokPixel: process.env.NEXT_PUBLIC_PASSTICKETS_TIKTOK_PIXEL_KEY
            ? { pixelId: process.env.NEXT_PUBLIC_PASSTICKETS_TIKTOK_PIXEL_KEY }
            : undefined,
        snapchatPixel: process.env.NEXT_PUBLIC_PASSTICKETS_SNAPCHAT_PIXEL_KEY
            ? { pixelId: process.env.NEXT_PUBLIC_PASSTICKETS_SNAPCHAT_PIXEL_KEY }
            : undefined,
        clarity: process.env.NEXT_PUBLIC_CLARITY_ID
            ? { projectId: process.env.NEXT_PUBLIC_CLARITY_ID }
            : undefined,
        posthog: process.env.NEXT_PUBLIC_POSTHOG_KEY
            ? {
                  apiKey: process.env.NEXT_PUBLIC_POSTHOG_KEY,
                  apiHost: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
              }
            : undefined,
    }
}

// Define the analytics context
interface AnalyticsContextType {
    trackEvent: (
        name: string,
        properties?: Record<string, any>,
        accountType?: AnalyticsAccountType,
        eventId?: string
    ) => void
    trackPageView: (
        url: string,
        accountType?: AnalyticsAccountType,
        eventId?: string,
        userProperties?: Record<string, any>
    ) => void
    getEventUserId: (eventId: string) => string | null
}

// Create context with default values
const AnalyticsContext = createContext<AnalyticsContextType>({
    trackEvent: () => {
        /* empty function by design */
    },
    trackPageView: () => {
        /* empty function by design */
    },
    getEventUserId: () => null,
})

// Hook to use analytics within components
export const useAnalytics = () => useContext(AnalyticsContext)

interface UnifiedAnalyticsProviderProps {
    children: React.ReactNode
}

const UnifiedAnalyticsProvider: React.FC<UnifiedAnalyticsProviderProps> = ({
    children,
}) => {
    const router = useRouter()
    const { user } = useUserContext()

    // State for dynamic configs
    const [platformConfig, setPlatformConfig] = useState<PlatformAnalyticsConfig | null>(
        cachedPlatformConfig
    )
    const [eventConfig, setEventConfig] = useState<EventAnalyticsConfig | null>(null)

    // Track if we've already fetched platform config
    const platformConfigFetched = useRef(false)
    const initializedClarityId = useRef<string | null>(null)

    // Get current event ID from the URL
    const getCurrentEventId = (): string | undefined => {
        if (
            router.pathname.includes("/events/[eventid]") ||
            router.pathname.includes("/preview/events/[eventid]")
        ) {
            return router.query.eventid as string
        }

        if (
            router.pathname.includes("/order/confirmed/[id]") ||
            router.pathname.includes("/order/failed/[id]") ||
            router.pathname.includes("/order-status/[orderId]")
        ) {
            const orderEventId =
                (router.query.event_id as string) ||
                (router.query.event as string)
            return orderEventId || undefined
        }

        // Handle special routes with hardcoded event IDs
        if (router.pathname.includes("guvali")) {
            return process.env.NEXT_PUBLIC_CURRENT_GUVALI_ID
        }

        if (router.pathname.includes("billory")) {
            return "66eaa9f10c3c0a69b8ab51c9"
        }

        return undefined
    }

    // Get the current event ID
    const currentEventId = getCurrentEventId()

    // Fetch platform config on mount (with TTL caching)
    useEffect(() => {
        if (typeof window === "undefined") return

        // Check TTL cache first
        const cached = getCachedPlatformConfig()
        if (cached) {
            setPlatformConfig(cached)
            platformConfigFetched.current = true
            return
        }

        // Skip if already fetched in this session (fallback check)
        if (platformConfigFetched.current && cachedPlatformConfig) {
            setPlatformConfig(cachedPlatformConfig)
            return
        }

        fetch("/api/analytics-config")
            .then((res) => res.json())
            .then((data) => {
                const config = data.platform || {}
                setCachedPlatformConfig(config)
                setPlatformConfig(config)
                platformConfigFetched.current = true
            })
            .catch(() => {
                // Fallback to env vars during migration
                const fallback = getFallbackPlatformConfig()
                setCachedPlatformConfig(fallback)
                setPlatformConfig(fallback)
            })
    }, [])

    // Fetch event config when currentEventId changes (with TTL caching)
    useEffect(() => {
        if (typeof window === "undefined") return

        if (!currentEventId) {
            setEventConfig(null)
            return
        }

        // Check TTL cache first
        const cached = getCachedEventConfig(currentEventId)
        if (cached) {
            setEventConfig(cached)
            return
        }

        // Fallback check for non-TTL cache
        if (eventConfigCache.has(currentEventId)) {
            setEventConfig(eventConfigCache.get(currentEventId) || null)
            return
        }

        fetch(`/api/analytics-config?eventId=${currentEventId}`)
            .then((res) => res.json())
            .then((data) => {
                const config = data.event || null
                if (config) {
                    setCachedEventConfig(currentEventId, config)
                }
                setEventConfig(config)
            })
            .catch(() => {
                setEventConfig(null)
            })
    }, [currentEventId])

    // Initialize analytics and track page views
    useEffect(() => {
        if (typeof window === "undefined") return

        // Initialize the analytics service
        analytics.initialize()

        // Initialize Microsoft Clarity if configured
        const clarityId = platformConfig?.clarity?.projectId
        if (clarityId && initializedClarityId.current !== clarityId) {
            try {
                if (document.getElementsByTagName("script").length > 0) {
                    clarity.init(clarityId)
                } else {
                    setTimeout(() => clarity.init(clarityId), 0)
                }
                initializedClarityId.current = clarityId
            } catch {
            }
        }

        // If user is logged in, identify them
        if (user && user.id) {
            analytics.identify({
                userId: user.id,
                traits: {
                    email: user.email || user.contact?.email,
                    name: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
                    firstName: user.first_name,
                    lastName: user.last_name,
                    phone: user.phone || user.contact?.phone,
                    gender: user.gender,
                },
            })
        }

        // Track router changes for page views
        const handleRouteChange = (url: string) => {
            // Main account tracking for all pages
            const userProperties = user
                ? {
                      userId: user.id,
                      userEmail: user.email || user.contact?.email,
                      userPhone: user.phone || user.contact?.phone,
                      email: user.email || user.contact?.email,
                      phone: user.phone || user.contact?.phone,
                      userName: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
                  }
                : {}

            const eventId = getCurrentEventId()
            analytics.pageView(url, "main", eventId, userProperties)
        }

        // Listen for route changes
        router.events.on("routeChangeComplete", handleRouteChange)

        // Track initial page view
        handleRouteChange(router.asPath)

        // Cleanup
        return () => {
            router.events.off("routeChangeComplete", handleRouteChange)
        }
    }, [router.events, router.asPath, user, platformConfig?.clarity?.projectId])

    // Context value with tracking functions
    const contextValue: AnalyticsContextType = {
        trackEvent: (name, properties = {}, accountType = "main", eventId) => {
            // Add user info to properties if available
            const userProperties = user
                ? {
                      userId: user.id,
                      userEmail: user.email || user.contact?.email,
                      userPhone: user.phone || user.contact?.phone,
                      email: user.email || user.contact?.email,
                      phone: user.phone || user.contact?.phone,
                      userName: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
                      ...properties,
                  }
                : properties

            // Extract event_id from properties if not passed as explicit parameter
            const resolvedEventId = eventId || properties?.event_id || currentEventId

            // Send to internal analytics backend
            enqueueEvent({
                event_name: name,
                event_category: accountType || 'general',
                event_id: resolvedEventId,
                properties: userProperties,
            })

            analytics.track({
                name,
                properties: userProperties,
                accountType,
                eventId: resolvedEventId,
            })
        },

        trackPageView: (url, accountType = "main", eventId, userProperties) => {
            const userPropertiesToUse = user
                ? {
                      userId: user.id,
                      userEmail: user.email || user.contact?.email,
                      userPhone: user.phone || user.contact?.phone,
                      email: user.email || user.contact?.email,
                      phone: user.phone || user.contact?.phone,
                      userName: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
                      ...(userProperties || {}),
                  }
                : userProperties || {}

            // Send to internal analytics backend
            enqueueEvent({
                event_name: 'page_view',
                event_category: accountType || 'general',
                event_id: eventId || currentEventId,
                properties: userPropertiesToUse,
            })

            analytics.pageView(
                url,
                accountType,
                eventId || currentEventId,
                userPropertiesToUse
            )
        },

        getEventUserId: (eventId) => {
            return analytics.getEventUserId(eventId)
        },
    }

    // Get effective config (platform config, fallback to env vars if not loaded)
    const effectiveConfig = platformConfig || getFallbackPlatformConfig()

    useEffect(() => {
        if (typeof window === "undefined") return

        const facebookPixels = [
            effectiveConfig.facebookPixel?.pixelId,
            eventConfig?.facebookPixel?.pixelId,
        ].filter((pixelId): pixelId is string => Boolean(pixelId))

        const tiktokPixels = [
            effectiveConfig.tiktokPixel?.pixelId,
            eventConfig?.tiktokPixel?.pixelId,
        ].filter((pixelId): pixelId is string => Boolean(pixelId))

        const snapchatPixels = [
            effectiveConfig.snapchatPixel?.pixelId,
            eventConfig?.snapchatPixel?.pixelId,
        ].filter((pixelId): pixelId is string => Boolean(pixelId))

        window.__analyticsFacebookPixels = Array.from(new Set(facebookPixels))
        window.__analyticsTikTokPixels = Array.from(new Set(tiktokPixels))
        window.__analyticsSnapchatPixels = Array.from(new Set(snapchatPixels))
    }, [
        effectiveConfig.facebookPixel?.pixelId,
        effectiveConfig.tiktokPixel?.pixelId,
        effectiveConfig.snapchatPixel?.pixelId,
        eventConfig?.facebookPixel?.pixelId,
        eventConfig?.tiktokPixel?.pixelId,
        eventConfig?.snapchatPixel?.pixelId,
    ])

    // Don't render scripts until config is loaded (non-blocking)
    // This prevents flash of untracked content while still being performant

    return (
        <AnalyticsContext.Provider value={contextValue}>
            {/* Google Analytics - Primary */}
            {effectiveConfig.googleAnalytics?.trackingId && (
                <Script
                    src={`https://www.googletagmanager.com/gtag/js?id=${effectiveConfig.googleAnalytics.trackingId}`}
                    strategy="afterInteractive"
                />
            )}
            {effectiveConfig.googleAnalytics?.trackingId && (
                <Script id="google-analytics" strategy="afterInteractive">
                    {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${effectiveConfig.googleAnalytics.trackingId}');
          `}
                </Script>
            )}

            {/* Google Analytics - Secondary */}
            {effectiveConfig.googleAnalyticsSecondary?.trackingId && (
                <Script
                    src={`https://www.googletagmanager.com/gtag/js?id=${effectiveConfig.googleAnalyticsSecondary.trackingId}`}
                    strategy="afterInteractive"
                />
            )}
            {effectiveConfig.googleAnalyticsSecondary?.trackingId && (
                <Script id="google-analytics-secondary" strategy="afterInteractive">
                    {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config1', '${effectiveConfig.googleAnalyticsSecondary.trackingId}');
          `}
                </Script>
            )}

            {/* Facebook Pixel - Platform */}
            {effectiveConfig.facebookPixel?.pixelId && !loadedScripts.has(`facebook-pixel-${effectiveConfig.facebookPixel.pixelId}`) && (() => { loadedScripts.add(`facebook-pixel-${effectiveConfig.facebookPixel.pixelId}`); return true; })() && (
                <Script id={`facebook-pixel-${effectiveConfig.facebookPixel.pixelId}`} strategy="afterInteractive">
                    {buildFacebookPixelScript(
                        effectiveConfig.facebookPixel.pixelId
                    )}
                </Script>
            )}

            {/* Facebook Pixel - Event-specific (runs alongside platform pixel) */}
            {eventConfig?.facebookPixel?.pixelId &&
                eventConfig.facebookPixel.pixelId !== effectiveConfig.facebookPixel?.pixelId &&
                !loadedScripts.has(`facebook-pixel-event-${eventConfig.facebookPixel.pixelId}`) &&
                (() => { loadedScripts.add(`facebook-pixel-event-${eventConfig.facebookPixel.pixelId}`); return true; })() && (
                <Script id={`facebook-pixel-event-${eventConfig.facebookPixel.pixelId}`} strategy="afterInteractive">
                    {buildFacebookPixelScript(
                        eventConfig.facebookPixel.pixelId
                    )}
                </Script>
            )}

            {/* TikTok Pixel - Platform */}
            {effectiveConfig.tiktokPixel?.pixelId && !loadedScripts.has(`tiktok-pixel-${effectiveConfig.tiktokPixel.pixelId}`) && (() => { loadedScripts.add(`tiktok-pixel-${effectiveConfig.tiktokPixel.pixelId}`); return true; })() && (
                <Script id={`tiktok-pixel-${effectiveConfig.tiktokPixel.pixelId}`} strategy="afterInteractive">
                    {buildTikTokPixelScript(
                        effectiveConfig.tiktokPixel.pixelId
                    )}
                </Script>
            )}

            {/* TikTok Pixel - Event-specific (runs alongside platform pixel) */}
            {eventConfig?.tiktokPixel?.pixelId &&
                eventConfig.tiktokPixel.pixelId !== effectiveConfig.tiktokPixel?.pixelId &&
                !loadedScripts.has(`tiktok-pixel-event-${eventConfig.tiktokPixel.pixelId}`) &&
                (() => { loadedScripts.add(`tiktok-pixel-event-${eventConfig.tiktokPixel.pixelId}`); return true; })() && (
                <Script id={`tiktok-pixel-event-${eventConfig.tiktokPixel.pixelId}`} strategy="afterInteractive">
                    {buildTikTokPixelScript(
                        eventConfig.tiktokPixel.pixelId
                    )}
                </Script>
            )}

            {/* Snapchat Pixel - Platform */}
            {effectiveConfig.snapchatPixel?.pixelId && !loadedScripts.has(`snapchat-pixel-${effectiveConfig.snapchatPixel.pixelId}`) && (() => { loadedScripts.add(`snapchat-pixel-${effectiveConfig.snapchatPixel.pixelId}`); return true; })() && (
                <Script id={`snapchat-pixel-${effectiveConfig.snapchatPixel.pixelId}`} strategy="afterInteractive">
                    {`
            (function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function()
            {a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};
            a.queue=[];var s='script';r=t.createElement(s);r.async=!0;
            r.src=n;var u=t.getElementsByTagName(s)[0];
            u.parentNode.insertBefore(r,u);})(window,document,
            'https://sc-static.net/scevent.min.js');
            snaptr('init', '${effectiveConfig.snapchatPixel.pixelId}');
            snaptr('track', 'PAGE_VIEW');
          `}
                </Script>
            )}

            {/* Snapchat Pixel - Event-specific (runs alongside platform pixel) */}
            {eventConfig?.snapchatPixel?.pixelId &&
                eventConfig.snapchatPixel.pixelId !== effectiveConfig.snapchatPixel?.pixelId &&
                !loadedScripts.has(`snapchat-pixel-event-${eventConfig.snapchatPixel.pixelId}`) &&
                (() => { loadedScripts.add(`snapchat-pixel-event-${eventConfig.snapchatPixel.pixelId}`); return true; })() && (
                <Script id={`snapchat-pixel-event-${eventConfig.snapchatPixel.pixelId}`} strategy="afterInteractive">
                    {`
            (function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function()
            {a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};
            a.queue=[];var s='script';r=t.createElement(s);r.async=!0;
            r.src=n;var u=t.getElementsByTagName(s)[0];
            u.parentNode.insertBefore(r,u);})(window,document,
            'https://sc-static.net/scevent.min.js');
            snaptr('init', '${eventConfig.snapchatPixel.pixelId}');
            snaptr('track', 'PAGE_VIEW');
          `}
                </Script>
            )}

            {/* PostHog */}
            {effectiveConfig.posthog?.apiKey && (
                <Script id="posthog-script" strategy="afterInteractive">
                    {`
            !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
            posthog.init('${effectiveConfig.posthog.apiKey}', {api_host: '${effectiveConfig.posthog.apiHost || "https://us.i.posthog.com"}'});
          `}
                </Script>
            )}

            {children}
        </AnalyticsContext.Provider>
    )
}

export default UnifiedAnalyticsProvider
